const HEX_COLOR_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const PRESETS = new Set(['classic', 'hero', 'spotlight', 'minimal']);
const TONES = new Set(['neutral', 'info', 'success', 'warning', 'critical']);
const IMAGE_POSITIONS = new Set(['left', 'right', 'top']);
const IMAGE_FITS = new Set(['cover', 'contain']);
const BUTTON_STYLES = new Set(['solid', 'outline', 'ghost']);
const TEXT_ALIGNS = new Set(['left', 'center', 'right']);
const CONTENT_ALIGNS = new Set(['start', 'center', 'end']);

const ACCENT_BY_TONE = {
  neutral: '#6366f1',
  info: '#0284c7',
  success: '#16a34a',
  warning: '#d97706',
  critical: '#dc2626',
};

const PRESET_DEFAULTS = {
  classic: { accent: '#6366f1' },
  hero: { accent: '#7c3aed' },
  spotlight: { accent: '#0ea5e9' },
  minimal: { accent: '#334155' },
};

const trimString = (value) => (typeof value === 'string' ? value.trim() : '');

const URL_PROTOCOL_RE = /^[a-z][a-z0-9+.-]*:\/\//i;

const unwrapUrlValue = (value) => {
  let raw = trimString(value);
  if (!raw) return '';

  if (
    (raw.startsWith('"') && raw.endsWith('"')) ||
    (raw.startsWith("'") && raw.endsWith("'"))
  ) {
    raw = raw.slice(1, -1).trim();
  }

  const markdownImageMatch = raw.match(/!\[[^\]]*]\(([^)\s]+)\)/i);
  if (markdownImageMatch?.[1]) {
    raw = markdownImageMatch[1].trim();
  }

  const htmlImgMatch = raw.match(/src\s*=\s*["']([^"']+)["']/i);
  if (htmlImgMatch?.[1]) {
    raw = htmlImgMatch[1].trim();
  }

  return raw;
};

const withHttpProtocol = (value) => {
  if (!value) return value;
  if (URL_PROTOCOL_RE.test(value)) return value;
  if (value.startsWith('//')) return `https:${value}`;
  if (/^[\w.-]+\.[a-z]{2,}([/:?#]|$)/i.test(value)) return `https://${value}`;
  return value;
};

const parseUrlSafely = (value) => {
  try {
    return new URL(value);
  } catch (_firstError) {
    try {
      return new URL(encodeURI(value));
    } catch (_secondError) {
      return null;
    }
  }
};

const normalizeGoogleDriveUrl = (parsed) => {
  const fileMatch = parsed.pathname.match(/\/file\/d\/([^/]+)/);
  const fileId =
    fileMatch?.[1] ||
    parsed.searchParams.get('id') ||
    parsed.searchParams.get('fileId') ||
    null;
  if (!fileId) return null;
  return `https://drive.google.com/uc?export=view&id=${encodeURIComponent(fileId)}`;
};

export const normalizeHexColor = (value) => {
  const raw = trimString(value);
  if (!raw) return '';
  return HEX_COLOR_RE.test(raw) ? raw.toLowerCase() : '';
};

const toSafeHttpUrlInternal = (value, depth = 0) => {
  if (depth > 2) return null;

  const raw = withHttpProtocol(unwrapUrlValue(value));
  if (!raw) return null;

  const parsed = parseUrlSafely(raw);
  if (!parsed) return null;

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return null;
  }

  const nestedCandidate =
    parsed.searchParams.get('imgurl') ||
    parsed.searchParams.get('mediaurl') ||
    parsed.searchParams.get('image_url') ||
    null;
  if (nestedCandidate) {
    const nestedUrl = toSafeHttpUrlInternal(nestedCandidate, depth + 1);
    if (nestedUrl) return nestedUrl;
  }

  const host = parsed.hostname.toLowerCase();
  if (parsed.protocol === 'http:' && host !== 'localhost' && host !== '127.0.0.1') {
    parsed.protocol = 'https:';
  }

  if (host === 'drive.google.com' || host === 'docs.google.com') {
    const normalizedDriveUrl = normalizeGoogleDriveUrl(parsed);
    if (normalizedDriveUrl) return normalizedDriveUrl;
  }

  if (host.endsWith('dropbox.com')) {
    parsed.hostname = 'dl.dropboxusercontent.com';
    parsed.searchParams.delete('dl');
    parsed.searchParams.set('raw', '1');
    return parsed.toString();
  }

  if (host === 'www.google.com' && parsed.pathname.startsWith('/imgres')) {
    const nestedUrl = toSafeHttpUrlInternal(parsed.searchParams.get('imgurl'), depth + 1);
    if (nestedUrl) return nestedUrl;
  }

  return parsed.toString();
};

export const toSafeHttpUrl = (value) => toSafeHttpUrlInternal(value, 0);

const clampNumber = (value, min, max) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return Math.max(min, Math.min(max, num));
};

const getRawDesign = (input) => {
  if (!input || typeof input !== 'object') return {};
  const data = input.data && typeof input.data === 'object' ? input.data : {};
  if (data.design && typeof data.design === 'object') {
    return data.design;
  }
  if (input.design && typeof input.design === 'object') {
    return input.design;
  }
  return data && Object.keys(data).length ? data : input;
};

export const resolveNotificationDesign = (input) => {
  const raw = getRawDesign(input);

  const presetValue = trimString(raw.preset || raw.style).toLowerCase();
  const preset = PRESETS.has(presetValue) ? presetValue : 'classic';

  const toneValue = trimString(raw.tone).toLowerCase();
  const tone = TONES.has(toneValue) ? toneValue : 'neutral';

  const accentColor =
    normalizeHexColor(raw.accentColor) ||
    ACCENT_BY_TONE[tone] ||
    PRESET_DEFAULTS[preset]?.accent ||
    '#6366f1';

  const buttonStyleValue = trimString(raw.buttonStyle).toLowerCase();
  const buttonStyle = BUTTON_STYLES.has(buttonStyleValue) ? buttonStyleValue : 'solid';

  const imagePositionValue = trimString(raw.imagePosition).toLowerCase();
  const imagePosition = IMAGE_POSITIONS.has(imagePositionValue) ? imagePositionValue : 'left';

  const imageFitValue = trimString(raw.imageFit).toLowerCase();
  const imageFit = IMAGE_FITS.has(imageFitValue) ? imageFitValue : 'cover';

  const autoDismissSec = clampNumber(
    raw.autoDismissSec ??
      (raw.autoDismissMs !== undefined && raw.autoDismissMs !== null
        ? Number(raw.autoDismissMs) / 1000
        : null),
    3,
    60,
  );
  const autoDismissMs = Math.round((autoDismissSec || 7) * 1000);

  const textAlignValue = trimString(raw.textAlign).toLowerCase();
  const textAlign = TEXT_ALIGNS.has(textAlignValue) ? textAlignValue : 'left';

  const contentAlignValue = trimString(raw.contentAlign).toLowerCase();
  const contentAlign = CONTENT_ALIGNS.has(contentAlignValue) ? contentAlignValue : 'start';

  return {
    preset,
    tone,
    eyebrow: trimString(raw.eyebrow),
    badge: trimString(raw.badge),
    subtitle: trimString(raw.subtitle),
    footer: trimString(raw.footer),
    icon: trimString(raw.icon),
    accentColor,
    backgroundColor: normalizeHexColor(raw.backgroundColor),
    textColor: normalizeHexColor(raw.textColor),
    bodyColor: normalizeHexColor(raw.bodyColor),
    ctaLabel: trimString(raw.ctaLabel),
    ctaUrl: toSafeHttpUrl(raw.ctaUrl),
    secondaryCtaLabel: trimString(raw.secondaryCtaLabel),
    secondaryCtaUrl: toSafeHttpUrl(raw.secondaryCtaUrl),
    imageUrl: toSafeHttpUrl(raw.imageUrl),
    imagePosition,
    imageFit,
    buttonStyle,
    textAlign,
    contentAlign,
    autoDismissMs,
    autoDismissSec: Math.round(autoDismissMs / 1000),
  };
};

export const buildNotificationDesignPayload = (form) => {
  const design = resolveNotificationDesign(form || {});
  const payload = {
    preset: design.preset,
    tone: design.tone,
    accentColor: design.accentColor,
    buttonStyle: design.buttonStyle,
    imagePosition: design.imagePosition,
    imageFit: design.imageFit,
    textAlign: design.textAlign,
    contentAlign: design.contentAlign,
    autoDismissSec: design.autoDismissSec,
  };

  if (design.eyebrow) payload.eyebrow = design.eyebrow;
  if (design.badge) payload.badge = design.badge;
  if (design.subtitle) payload.subtitle = design.subtitle;
  if (design.footer) payload.footer = design.footer;
  if (design.icon) payload.icon = design.icon;
  if (design.backgroundColor) payload.backgroundColor = design.backgroundColor;
  if (design.textColor) payload.textColor = design.textColor;
  if (design.bodyColor) payload.bodyColor = design.bodyColor;
  if (design.ctaLabel) payload.ctaLabel = design.ctaLabel;
  if (design.ctaUrl) payload.ctaUrl = design.ctaUrl;
  if (design.secondaryCtaLabel) payload.secondaryCtaLabel = design.secondaryCtaLabel;
  if (design.secondaryCtaUrl) payload.secondaryCtaUrl = design.secondaryCtaUrl;
  if (design.imageUrl) payload.imageUrl = design.imageUrl;

  return payload;
};
